import { Button } from '@florencelabs/button';
import { DialogTitle, Modal, Dialog } from '@florencelabs/modal-2';
import { ReactElement, useId, useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  type InactivityModalState,
  type InactivityModalContent,
} from '@provider-directory/hooks/useInactivity';

export const formatCountdown = (totalSeconds: number): string => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const useCountdown = (
  getRemainingTime: () => number,
  isActive: boolean,
): number => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    const update = (): void => {
      const remaining = Math.ceil(getRemainingTime() / 1000);
      setSeconds(Math.max(0, remaining));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime, isActive]);

  return seconds;
};

const BUTTON_ACTION_MAP: Record<
  InactivityModalContent['buttonAction'],
  string
> = {
  extend: 'modal-extend-btn',
  dismiss: 'modal-dismiss-btn',
  reload: 'modal-reload-btn',
};

interface InactivityModalProps {
  isOpen: boolean;
  modalState: InactivityModalState;
  content: InactivityModalContent;
  onExtendSession: () => void;
  onDismiss: () => void;
  onReload: () => void;
  remainingExtensions: number;
  getRemainingTime: () => number;
}

export const InactivityModal = ({
  isOpen,
  modalState,
  content,
  onExtendSession,
  onDismiss,
  onReload,
  remainingExtensions,
  getRemainingTime,
}: InactivityModalProps): ReactElement => {
  const { t } = useTranslation();
  const isWarning = modalState === 'warning';
  const isWarningFinal = modalState === 'warning-final';
  const isExpired = modalState === 'expired';

  const countdown = useCountdown(getRemainingTime, isWarning || isWarningFinal);
  const formattedCountdown = formatCountdown(countdown);

  const bodyId = useId();

  const buttonActionHandlers: Record<
    InactivityModalContent['buttonAction'],
    () => void
  > = {
    extend: onExtendSession,
    dismiss: onDismiss,
    reload: onReload,
  };

  const title = isExpired
    ? t(content.titleKey)
    : t(content.titleKey, { countdown: formattedCountdown });

  const body = isWarning ? (
    <Trans
      i18nKey={content.bodyKey}
      values={{ count: remainingExtensions }}
      components={{ bold: <strong /> }}
    />
  ) : (
    t(content.bodyKey)
  );

  return (
    <Modal
      isOpen={isOpen}
      // All states require explicit button interaction — no overlay click or Escape dismiss.
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      overlayClassName={'bg-black/60 z-50'}
    >
      <Dialog
        role={'alertdialog'}
        aria-describedby={bodyId}
        className={
          'min-h-[222px] w-[327px] rounded-lg bg-white text-left tablet:w-[416px]'
        }
      >
        <div
          data-testid={'inactivity-modal-container'}
          className={
            'flex h-full flex-col rounded-lg px-6 pt-6 mobile:py-10 tablet:px-10'
          }
        >
          <div className={'flex w-full flex-col'}>
            <div className={'w-full'}>
              <div className={'pb-4'}>
                <DialogTitle
                  className={'font-serif text-display-large text-neutral-950'}
                >
                  {title}
                </DialogTitle>
              </div>
              <div>
                <div
                  id={bodyId}
                  aria-live={'polite'}
                  className={'text-body-medium text-neutral-950'}
                >
                  {body}
                </div>
              </div>
            </div>
          </div>
          <div className={'pt-8 tablet:pt-10'}>
            <Button
              testId={BUTTON_ACTION_MAP[content.buttonAction]}
              variant={'clientMain'}
              className={'w-full text-button-small'}
              onPress={buttonActionHandlers[content.buttonAction]}
            >
              {t(content.buttonLabelKey)}
            </Button>
          </div>
        </div>
      </Dialog>
    </Modal>
  );
};
